import { AUTH_OPT_IN_HEADERS } from "../services/authSession";
import { useApiMutation, useApiQuery } from "../services/react-query";

// Calls that answer with a session send `AUTH_OPT_IN_HEADERS`, so a
// sessions-aware API answers with an access and refresh token pair (an older
// one ignores it). Either body goes to `applySignInResponse` as is.

// Invalidates nothing on its own: the caller has to store the token first,
// otherwise the refetched currentUser query goes out anonymously, 401s and —
// with retries off for 4xx — leaves the app signed out on /login.
export const useLogin = () =>
  useApiMutation({
    fetchOptions: {
      url: "/auth/login",
      method: "POST",
      headers: AUTH_OPT_IN_HEADERS,
    },
    invalidateQueryKeys: [],
    hideNotification: true,
  });

export const useSendResetPasswordEmail = () =>
  useApiMutation({
    fetchOptions: {
      url: "/auth/forgot-password",
      method: "POST",
    },
    invalidateQueryKeys: [],
    hideNotification: true,
  });

export const useSendInviteMail = (userPublicId) =>
  useApiMutation({
    fetchOptions: {
      url: `/auth/invite/${userPublicId}`,
      method: "POST",
    },
    invalidateQueryKeys: [],
    hideNotification: true,
  });

export const useGetUserByInviteToken = (token) =>
  useApiQuery({
    queryOptions: { queryKey: ["auth"] },
    fetchOptions: {
      url: `/auth/${token}`,
      method: "GET",
    },
  });

export const useSetPassword = () =>
  useApiMutation({
    fetchOptions: {
      url: "/auth/set-password",
      method: "POST",
    },
  });

export const useGetUserByForgotPasswordToken = (token) =>
  useApiQuery({
    queryOptions: { queryKey: ["auth"] },
    fetchOptions: {
      url: `/auth/forgot-password/${token}`,
      method: "GET",
    },
  });

export const useGoogleLogin = (data) =>
  useApiMutation({
    fetchOptions: {
      url: "/auth/google-sign-in",
      method: "POST",
      headers: AUTH_OPT_IN_HEADERS,
      body: { data },
    },
  });

export const useAppleSignIn = (data) =>
  useApiMutation({
    fetchOptions: {
      url: "/auth/apple-sign-in",
      method: "POST",
      headers: AUTH_OPT_IN_HEADERS,
      body: { data },
    },
  });

// Email sign-in: the first call emails a 6-digit code (always "ok", account
// or not), the second trades it for a session.
export const useEmailSignInStart = () =>
  useApiMutation({
    fetchOptions: {
      url: "/auth/email-sign-in/start",
      method: "POST",
    },
    hideNotification: true,
  });

export const useEmailSignInVerify = () =>
  useApiMutation({
    fetchOptions: {
      url: "/auth/email-sign-in/verify",
      method: "POST",
      headers: AUTH_OPT_IN_HEADERS,
    },
    // As for the password login: the caller stores the session and then
    // refetches. Refetching here, before the session is stored, raced that
    // refetch and left the person on the login screen until a reload.
    invalidateQueryKeys: [],
    hideNotification: true,
  });
